import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiDelete, apiGet, apiPatch } from "@/lib/apiClient";
import { useFinanceAccountPaths } from "@/lib/financeRouteContext";
import { FinanceErrorBanner } from "@/lib/financeUi";
import { ArrowLeft, User } from "lucide-react";

type FinanceCustomer = {
  id: string;
  name?: string;
  email?: string | null;
  phone?: string | null;
  companyName?: string | null;
  address?: string | null;
  currency?: string | null;
};

export function FinanceCustomerDetailPage() {
  const navigate = useNavigate();
  const { listPath, listLabel, salesAccounts } = useFinanceAccountPaths();
  const invoicesPath = salesAccounts ? "/finance/sales/invoices" : "/finance/invoices";
  const { customerId: idParam } = useParams<{ customerId: string }>();
  const customerId = idParam ? decodeURIComponent(idParam) : "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customer, setCustomer] = useState<FinanceCustomer | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyName, setCompanyName] = useState("");

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await apiGet<FinanceCustomer>(`/api/finance/customers/${encodeURIComponent(customerId)}`);
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error || "Could not load customer.");
        setCustomer(null);
      } else {
        setError(null);
        const c = res.data;
        setCustomer(c);
        if (c) {
          setName(c.name || "");
          setEmail(c.email || "");
          setPhone(c.phone || "");
          setCompanyName(c.companyName || "");
        }
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  const onSave = async () => {
    if (!customer?.id) return;
    setSaving(true);
    setError(null);
    const res = await apiPatch<FinanceCustomer>(`/api/finance/customers/${encodeURIComponent(customer.id)}`, {
      name: name.trim(),
      email: email.trim() || null,
      phone: phone.trim() || null,
      companyName: companyName.trim() || null,
    });
    setSaving(false);
    if (!res.ok) {
      setError(res.error || "Save failed.");
      return;
    }
    setCustomer(res.data);
    setEditing(false);
  };

  const onDelete = async () => {
    if (!customer?.id) return;
    if (!window.confirm(`Delete customer "${customer.name}"? This cannot be undone.`)) return;
    setSaving(true);
    const res = await apiDelete<{ ok?: boolean }>(`/api/finance/customers/${encodeURIComponent(customer.id)}`);
    setSaving(false);
    if (!res.ok) {
      setError(res.error || "Delete failed.");
      return;
    }
    void navigate(listPath);
  };

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <Link
        to={listPath}
        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        {listLabel}
      </Link>

      {loading ? <p className="text-sm text-slate-500">Loading customer…</p> : null}
      {error ? <FinanceErrorBanner message={error} /> : null}

      {customer && !loading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-950/40">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <User className="h-8 w-8 text-indigo-600" aria-hidden />
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {customer.name || (salesAccounts ? "Account" : "Customer")}
                </h1>
                <p className="font-mono text-xs text-slate-500">{customer.id}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {editing ? (
                <>
                  <button
                    type="button"
                    onClick={() => void onSave()}
                    disabled={saving || !name.trim()}
                    className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(false);
                      setName(customer.name || "");
                      setEmail(customer.email || "");
                      setPhone(customer.phone || "");
                      setCompanyName(customer.companyName || "");
                    }}
                    className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to={invoicesPath}
                    className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
                  >
                    Invoices
                  </Link>
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => void onDelete()}
                    disabled={saving}
                    className="rounded-md border border-rose-200 px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-50 dark:border-rose-900/50 dark:text-rose-300"
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>

          {editing ? (
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="font-medium text-slate-700 dark:text-slate-300">Name</span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-slate-700 dark:text-slate-300">Company</span>
                <input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-slate-700 dark:text-slate-300">Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-slate-700 dark:text-slate-300">Phone</span>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                />
              </label>
            </div>
          ) : (
            <dl className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[
                { label: "Company", value: customer.companyName?.trim() || "—" },
                { label: "Email", value: customer.email?.trim() || "—" },
                { label: "Phone", value: customer.phone?.trim() || "—" },
                { label: "Address", value: customer.address?.trim() || "—" },
                { label: "Currency", value: customer.currency || "—" },
              ].map((row) => (
                <div key={row.label}>
                  <dt className="text-xs font-semibold uppercase text-slate-500">{row.label}</dt>
                  <dd className="mt-1 text-sm font-medium text-slate-900 dark:text-white">{row.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      ) : null}
    </div>
  );
}
